import React, { useState } from 'react';
import {
  Table,
  Button,
  Select,
  Input,
  Tag,
  Space,
  Popconfirm,
  Form,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { InspectionCheckpoint } from '../../services/qc-workflow.service';
import {
  useInspectionCheckpoints,
  useAddCheckpoint,
  useUpdateCheckpoint,
  useDeleteCheckpoint,
} from '../../hooks/useQCWorkflow';

const CHECKPOINT_TYPES = [
  'Visual Inspection',
  'Material Verification',
  'MTC Verification',
  'Functional Check',
  'Welding/NDE Check',
  'Quantity Verification',
  'Marking/Tagging',
  'Customer/TPI Witness',
  'Custom',
];

const RESULT_OPTIONS = ['Pass', 'Fail', 'Conditional', 'N/A', 'Pending'] as const;

const RESULT_COLORS: Record<string, string> = {
  Pass: 'green',
  Fail: 'red',
  Conditional: 'orange',
  'N/A': 'default',
  Pending: 'blue',
};

interface InspectionCheckpointsProps {
  inspectionId: string;
  readOnly?: boolean;
}

const InspectionCheckpoints: React.FC<InspectionCheckpointsProps> = ({
  inspectionId,
  readOnly = false,
}) => {
  const { data: checkpoints = [], isLoading } = useInspectionCheckpoints(inspectionId);
  const addMutation = useAddCheckpoint();
  const updateMutation = useUpdateCheckpoint();
  const deleteMutation = useDeleteCheckpoint();

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();

  const handleAdd = async (values: any) => {
    await addMutation.mutateAsync({
      inspectionId,
      data: {
        checkpoint_type: values.checkpoint_type,
        description: values.description,
        acceptance_criteria: values.acceptance_criteria,
        result: values.result || 'Pending',
        remarks: values.remarks,
        sequence_number: checkpoints.length,
      },
    });
    addForm.resetFields();
    setAddModalVisible(false);
  };

  const handleResultChange = (checkpointId: string, result: string) => {
    updateMutation.mutate({
      checkpointId,
      inspectionId,
      data: { result },
    });
  };

  const handleRemarksChange = (checkpointId: string, remarks: string) => {
    updateMutation.mutate({
      checkpointId,
      inspectionId,
      data: { remarks },
    });
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'sequence_number',
      key: 'seq',
      width: 50,
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: 'Type',
      dataIndex: 'checkpoint_type',
      key: 'checkpoint_type',
      width: 180,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Acceptance Criteria',
      dataIndex: 'acceptance_criteria',
      key: 'acceptance_criteria',
      ellipsis: true,
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      width: 140,
      render: (result: string, record: InspectionCheckpoint) =>
        readOnly ? (
          <Tag color={RESULT_COLORS[result]}>{result}</Tag>
        ) : (
          <Select
            value={result}
            style={{ width: 130 }}
            onChange={(val) => handleResultChange(record.id, val)}
            loading={updateMutation.isPending}
          >
            {RESULT_OPTIONS.map((r) => (
              <Select.Option key={r} value={r}>
                <Tag color={RESULT_COLORS[r]} style={{ margin: 0 }}>{r}</Tag>
              </Select.Option>
            ))}
          </Select>
        ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (remarks: string | null, record: InspectionCheckpoint) =>
        readOnly ? (
          remarks || '-'
        ) : (
          <Input.TextArea
            defaultValue={remarks || ''}
            autoSize={{ minRows: 1, maxRows: 3 }}
            onBlur={(e) => {
              if (e.target.value !== (remarks || '')) {
                handleRemarksChange(record.id, e.target.value);
              }
            }}
          />
        ),
    },
    ...(!readOnly
      ? [
          {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: any, record: InspectionCheckpoint) => (
              <Popconfirm
                title="Remove this checkpoint?"
                onConfirm={() => deleteMutation.mutate({ checkpointId: record.id, inspectionId })}
              >
                <Button type="text" danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const passCount = checkpoints.filter((c: InspectionCheckpoint) => c.result === 'Pass').length;
  const failCount = checkpoints.filter((c: InspectionCheckpoint) => c.result === 'Fail').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          {checkpoints.length > 0 && (
            <>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <span>{passCount} Pass</span>
              {failCount > 0 && (
                <>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>{failCount} Fail</span>
                </>
              )}
              <span style={{ color: '#999' }}>/ {checkpoints.length} total</span>
            </>
          )}
        </Space>
        {!readOnly && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
            size="small"
          >
            Add Checkpoint
          </Button>
        )}
      </div>

      <Table
        dataSource={checkpoints}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: 'No checkpoints yet — click "Add Checkpoint" to begin' }}
      />

      <Modal
        title="Add Inspection Checkpoint"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => addForm.submit()}
        confirmLoading={addMutation.isPending}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="checkpoint_type"
            label="Checkpoint Type"
            rules={[{ required: true, message: 'Select a type' }]}
          >
            <Select placeholder="Select checkpoint type">
              {CHECKPOINT_TYPES.map((t) => (
                <Select.Option key={t} value={t}>{t}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="acceptance_criteria" label="Acceptance Criteria">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InspectionCheckpoints;
